import { Alert, Button, Form, Input, Skeleton } from 'antd';
import { FC, useMemo } from 'react';
import { EasyField } from 'react-antd-formutil';
import { Navigate, useNavigate } from 'react-router-dom';
import Card, { CardButtonWrap } from 'components/Card';
import ErrorBox from 'components/ErrorBox';
import UploaderList from 'components/FileUploader/UploaderList';
import FormItem from 'components/FormItem';
import ScrollTop from 'components/ScrollTop';
import { Fade } from 'components/Transition';
import { useBrokerList, useTransferInfo } from 'modules/Transfer/hooks';
import { PageIdEnum, TransferFormProps } from 'modules/Transfer/withTransferForm';
import security from 'stores/security';
import { printf } from 'utils/i18n';
import { OTHER_BROKER_ID, otherBrokerOption } from '../../helper';
import { BrokerPageFormutil, CheckPageFormutil } from '../../type';
import style from './style.scss';

interface CheckProps extends TransferFormProps<CheckPageFormutil> {
    brokerPageId: PageIdEnum;
    onCheckSubmit: () => void;
}

const Check: FC<CheckProps> = ({ $formutil, getStorageDataByKey, brokerPageId, onCheckSubmit }) => {
    const navigate = useNavigate();
    const { $params } = $formutil;
    const { broker_id: brokerId } = getStorageDataByKey<BrokerPageFormutil>(brokerPageId);
    const isOtherBroker = brokerId === OTHER_BROKER_ID;
    const {
        data: brokerListData,
        error: brokerListError,
        isLoading: brokerListLoading,
        mutate: brokerListMutate
    } = useBrokerList({ getAllBroker: true });
    const {
        data: transferInfoData,
        error: transferInfoError,
        isLoading: transferInfoLoading,
        mutate: transferInfoMutate
    } = useTransferInfo();
    /**
     * 从接口返回的经纪公司列表中, 找到当前选中的这个经纪公司
     * PS: 接口返回不包含"Other"这个选项, "Other"选项是前端在拿到接口返回结果后, 在选择经纪公司的页面手动拼接的
     * 所以这里要特殊处理一下"Other"这个选项
     */
    const brokerageFirm = useMemo(() => {
        if (isOtherBroker) {
            return otherBrokerOption;
        }

        return (brokerListData?.items || []).find(item => item.id === brokerId);
    }, [brokerListData, brokerId, isOtherBroker]);

    if ((transferInfoError && !transferInfoData) || (brokerListError && !brokerListData)) {
        return (
            <ErrorBox
                error={transferInfoError || brokerListError}
                onClick={() => {
                    transferInfoError && transferInfoMutate();
                    brokerListError && brokerListMutate();
                }}
            />
        );
    }

    if (transferInfoLoading || brokerListLoading) {
        return <Skeleton active />;
    }

    if (!brokerageFirm) {
        return <Navigate to="../broker" replace />;
    }

    return (
        <Card form className="transfer-check-root">
            <ScrollTop />
            <div className="transfer-title">
                {__('Complete Account Details')}
                <div className={style.titleDesc}>
                    {isOtherBroker
                        ? __('Complete your delivering account information')
                        : printf(__('Complete your %s account information'), brokerageFirm.broker)}
                </div>
            </div>
            <Form noValidate layout="vertical" onFinish={onCheckSubmit}>
                <FormItem
                    required
                    name="broker_user_name"
                    label={__('Account Holder Name')}
                    $defaultValue={transferInfoData?.name || security.webAccountInfo?.real_name}
                    checker={val =>
                        /^[a-zA-Z-']+(\s[a-zA-Z-']+)*$/.test(val) || __('The Holder Name you entered is not valid')
                    }
                    validMessage={{ required: __('Please enter your account holder name') }}>
                    <Input placeholder={__('As shown on your account')} />
                </FormItem>
                <Fade
                    in={
                        !!($params.broker_user_name && security.webAccountInfo?.real_name !== $params.broker_user_name)
                    }>
                    <Alert
                        className="mb-4"
                        type="warning"
                        message={__(
                            'Are you sure you want to use the name you entered? This name does not match our records, which may cause your transfer to fail.'
                        )}
                    />
                </Fade>
                <FormItem
                    required
                    name="broker_account"
                    label={__('Account Number')}
                    checker={val => /^[a-zA-Z\d]+$/.test(val) || __('The Account Number you entered is not valid')}
                    validMessage={{ required: __('Please enter your account number') }}>
                    <Input placeholder={__('Please enter your account number')} />
                </FormItem>
                {isOtherBroker ? (
                    <>
                        <FormItem
                            required
                            name="company"
                            label={__('Delivering Firm Name')}
                            checker={val => /^[\w\s\-.',&]+$/.test(val) || __('The delivering firm name is not valid.')}
                            validMessage={{ required: __('Please enter your delivering firm name') }}>
                            <Input placeholder={__('Please enter your delivering firm name')} />
                        </FormItem>
                        <FormItem
                            name="dtc_number"
                            label={__('DTC Clearing #')}
                            required
                            checker={val =>
                                /^\d{4}$/.test(val) || __('The DTC clearing number should be a four digit number.')
                            }
                            validMessage={{ required: __('Please enter the DTC clearing number') }}>
                            <Input placeholder={__('Please enter your DTC Clearing #')} />
                        </FormItem>
                    </>
                ) : (
                    <>
                        <EasyField type="hidden" name="company" $defaultValue={brokerageFirm.company} />
                        <EasyField type="hidden" name="dtc_number" $defaultValue={brokerageFirm.dtc_number} />
                    </>
                )}
                <div className="ant-form-item-label">
                    {security.isIRAAccount
                        ? __('Upload Recent Statement and Photo ID')
                        : __('Upload Recent Statement')}
                </div>
                <UploaderList
                    maxCount={6}
                    type="deposit"
                    name="files"
                    extra={
                        <div className={style.uploadExtra}>
                            {security.isIRAAccount
                                ? printf(
                                      __(
                                          'Please upload your most recent %s statement, along with valid photo identification. If you do not have these documents on hand, please return later to upload the requested documentation.'
                                      ),
                                      brokerageFirm.broker
                                  )
                                : printf(
                                      __(
                                          'Please upload your most recent %s statement. If you do not have this document on hand, please return later to upload the requested documentation.'
                                      ),
                                      brokerageFirm.broker
                                  )}
                        </div>
                    }
                    bucket="customer-deposit"
                />
                <CardButtonWrap className={style.footer}>
                    <Button size="large" onClick={() => navigate(-1)}>
                        {__('Back')}
                    </Button>
                    <Button size="large" type="primary" htmlType="submit">
                        {__('Continue')}
                    </Button>
                </CardButtonWrap>
            </Form>
        </Card>
    );
};

export default Check;
